import React, { useContext, useMemo } from "react";
import { Offcanvas, Button } from "react-bootstrap";
import { FiCopy, FiX } from "react-icons/fi";
import moment from "moment";
import hljs from "highlight.js/lib/core";
import json from "highlight.js/lib/languages/json";
import "highlight.js/styles/github.css";
import { ToastrContext } from "../../../provider/toastrContext";

hljs.registerLanguage("json", json);

const preStyle = {
  background: "var(--bs-tertiary-bg, #f8f9fa)",
  border: "1px solid var(--bs-border-color, #dee2e6)",
  borderRadius: 4,
  padding: "8px 12px",
  margin: 0,
  fontSize: 12,
  lineHeight: 1.4,
  maxHeight: "60vh",
  overflow: "auto",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const MetaRow = ({ label, children }) => (
  <div className="row mb-1">
    <div className="col-4 text-muted small">{label}</div>
    <div className="col-8">{children}</div>
  </div>
);

const formatTs = (ts) => (ts ? moment(ts).format("DD-MM-YYYY HH:mm:ss") : "—");

const EventPayloadDrawer = ({ event, onClose }) => {
  const { showCopy, showError } = useContext(ToastrContext);

  const { jsonString, highlighted } = useMemo(() => {
    if (!event) return { jsonString: "", highlighted: "" };
    try {
      const s = JSON.stringify(event, null, 2);
      return {
        jsonString: s,
        highlighted: hljs.highlight(s, { language: "json" }).value,
      };
    } catch {
      const fallback = String(event);
      return { jsonString: fallback, highlighted: fallback };
    }
  }, [event]);

  const copyEventID = async () => {
    if (!event || !event.event_id) return;
    try {
      await navigator.clipboard.writeText(event.event_id);
      showCopy("Event ID copied");
    } catch {
      showError("Copy failed");
    }
  };

  const copyPayload = async () => {
    if (!jsonString) return;
    try {
      await navigator.clipboard.writeText(jsonString);
      showCopy("Payload copied");
    } catch {
      showError("Copy failed");
    }
  };

  return (
    <Offcanvas
      show={!!event}
      onHide={onClose}
      placement="end"
      style={{ width: 640 }}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          <div className="d-flex align-items-center">
            <code className="me-2">{event ? event.event_id : ""}</code>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={copyEventID}
              title="Copy event ID"
            >
              <FiCopy />
            </Button>
          </div>
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {event && (
          <>
            <div className="mb-3">
              <MetaRow label="Type">
                <code>{event.event_type}</code>
              </MetaRow>
              <MetaRow label="Livemode">
                <span
                  className={`badge bg-${event.livemode ? "primary" : "info"}`}
                >
                  {event.livemode ? "live" : "test"}
                </span>
              </MetaRow>
              <MetaRow label="Device">
                {event.idt_device != null ? event.idt_device : "—"}
              </MetaRow>
              <MetaRow label="API version">{event.api_version || "—"}</MetaRow>
              <MetaRow label="Created">{formatTs(event.ts_created)}</MetaRow>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <div className="text-muted small">Full envelope</div>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={copyPayload}
                title="Copy payload JSON"
              >
                <FiCopy className="align-middle me-1" />
                Copy JSON
              </Button>
            </div>
            <pre style={preStyle}>
              <code
                className="language-json hljs"
                dangerouslySetInnerHTML={{ __html: highlighted }}
              />
            </pre>
          </>
        )}
      </Offcanvas.Body>
      <div className="border-top p-2 text-end">
        <Button variant="secondary" size="sm" onClick={onClose}>
          <FiX className="align-middle me-1" />
          Close
        </Button>
      </div>
    </Offcanvas>
  );
};

export default EventPayloadDrawer;
